import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../data";
import { fehlerText } from "../../lib/fehler";
import { t } from "../../lib/sprache";

type Stand = { verified: boolean; required: boolean; email: string; last_sent_at: string | null };

/**
 * Hinweis oben im Portal, solange die eigene Adresse nicht bestätigt ist (0247, Anmeldung ohne Code): dezent, mit „Mail erneut
 * senden“ und „Adresse korrigieren“ (Tippfehler bei der Registrierung). Bis zur Bestätigung schickt das Konto nichts nach außen.
 */
export function AdresseHinweis() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["my_email_verification"], enabled: api.mode !== "demo", staleTime: 60_000, refetchOnWindowFocus: true,
    // Kam die Sitzung über einen Link aus einer Mail (etwa „Passwort vergessen“), gilt die Adresse als bestätigt (0250)
    queryFn: async () => {
      const s = await api.read<Stand | null>("my_email_verification");
      if (s?.required && !s.verified && (await api.rpc<boolean>("email_verified_from_session").catch(() => false))) return { ...s, verified: true, required: false };
      return s;
    } });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [korrigieren, setKorrigieren] = useState(false);
  const [neu, setNeu] = useState("");
  // Demo-Vorschau (?vorschau=adresse): so sieht der Hinweis für eine frische Registrierung aus
  const vorschau = api.mode === "demo" && typeof window !== "undefined" && new URLSearchParams(window.location.search).get("vorschau") === "adresse";
  const s = vorschau ? { verified: false, required: true, email: "lena.sommer@example.com", last_sent_at: null } : q.data;
  if (!s || !s.required || s.verified) return null;

  const neuLaden = () => { (api as { vergissBestaetigung?: () => void }).vergissBestaetigung?.(); void qc.invalidateQueries({ queryKey: ["my_email_verification"] }); };

  async function senden() {
    setBusy(true); setFehler(null); setStatus(null);
    try {
      const r = await api.rpc<{ status: string }>("email_verification_resend");
      setStatus(r.status === "gesendet" ? t("Wir haben dir den Link noch einmal geschickt. Schau auch im Spam-Ordner nach.", "We have sent you the link again. Also check your spam folder.")
        : r.status === "warten" ? t("Du hast gerade erst eine Mail bekommen. Bitte schau in dein Postfach oder versuch es in 10 Minuten noch einmal.", "You only just received an email. Please check your inbox or try again in 10 minutes.")
          : r.status === "grenze" ? t("Wir haben dir schon mehrere Mails geschickt. Bitte schreib uns an support@creatoroffice.de.", "We have already sent you several emails. Please write to support@creatoroffice.de.")
            : t("Deine Adresse ist bestätigt.", "Your address is confirmed."));
      if (r.status === "bestaetigt") neuLaden();
    } catch (e) { setFehler(fehlerText(e)); } finally { setBusy(false); }
  }
  async function speichern(e: FormEvent) {
    e.preventDefault();
    const mail = neu.trim();
    if (!mail) return;
    if (!api.kontoDienste) { setFehler(t("In der Demo lässt sich die Adresse nicht ändern.", "The address cannot be changed in the demo.")); return; }
    setBusy(true); setFehler(null); setStatus(null);
    try {
      await api.kontoDienste({ action: "adresse_korrigieren", email: mail });
      setKorrigieren(false); setNeu("");
      setStatus(t(`Geändert. Den Bestätigungslink haben wir an ${mail} geschickt.`, `Changed. We have sent the confirmation link to ${mail}.`));
      neuLaden();
    } catch (err) { setFehler(fehlerText(err)); } finally { setBusy(false); }
  }

  return (
    <div className="content tk-rahmen-hinweise">
    <div className="notice tk-adresse-hinweis" role="status">
      <p>
        <strong>{t("Bitte bestätige noch deine E-Mail-Adresse", "Please confirm your email address")}</strong>{" "}
        {t(`(${s.email}). Bis dahin verschickt CreatorOffice nichts in deinem Namen, also keine Einladungen, Verträge, Rechnungen oder Mails an Marken. Alles andere kannst du schon nutzen.`,
          `(${s.email}). Until then, CreatorOffice does not send anything on your behalf: no invitations, contracts, invoices or emails to brands. You can already use everything else.`)}
      </p>
      {korrigieren ? (
        <form className="row" onSubmit={(e) => void speichern(e)}>
          <label className="sr-only" htmlFor="adresse-neu">{t("Richtige E-Mail-Adresse", "Correct email address")}</label>
          <input id="adresse-neu" type="email" required autoComplete="email" value={neu} onChange={(e) => setNeu(e.target.value)} placeholder={t("richtige@adresse.de", "correct@address.com")} />
          <button type="submit" className="primary" disabled={busy}>{busy ? t("Wird gespeichert …", "Saving …") : t("Speichern und Link senden", "Save and send link")}</button>
          <button type="button" onClick={() => setKorrigieren(false)} disabled={busy}>{t("Abbrechen", "Cancel")}</button>
        </form>
      ) : (
        <div className="row">
          <button type="button" onClick={() => void senden()} disabled={busy}>{busy ? t("Wird gesendet …", "Sending …") : t("Mail erneut senden", "Send email again")}</button>
          <button type="button" onClick={() => { setKorrigieren(true); setNeu(s.email); setStatus(null); }} disabled={busy}>{t("Adresse korrigieren", "Correct address")}</button>
        </div>
      )}
      {status && <p role="status">{status}</p>}
      {fehler && <p className="tk-error" role="alert">{fehler}</p>}
    </div>
    </div>
  );
}
